import type { CssVars } from "@/utils/css-vars";

/**
 * What the app remembers between launches so the first frame is already right.
 *
 * The theme the app paints with is not knowable at boot: it comes from `/theme-config` over the
 * network, and the appearance on top of it comes from `Preferences`, which is async on native. So
 * the honest first paint is a white screen that turns into the brand a moment later — and if the
 * employee chose dark, a white flash straight into the eyes. That flash is the single most visible
 * thing about a dark mode; it is worth a cache to remove it.
 *
 * These two keys hold the last resolved answer, written by the theme provider whenever it changes
 * and read by the inline script at the top of `index.html` before React exists. `localStorage` and
 * not `Preferences`, because only a synchronous read can beat the first paint — the same value is
 * still persisted properly through the preferences store, this is a mirror and never the source.
 *
 * The script in `index.html` cannot import from here, so it repeats these two key names as string
 * literals. Change one, change both.
 */
export const BOOT_APPEARANCE_KEY = "boomer.appearance";
export const BOOT_THEME_VARS_KEY = "boomer.theme-vars";

export type ResolvedAppearance = "light" | "dark";

/**
 * Remember the painted result for the next cold start.
 *
 * Storage can throw rather than return — a full quota, Safari's private mode, a WebView with site
 * data disabled. None of that is worth an error boundary: the only cost of failing here is the
 * flash we were trying to avoid, so it fails silently and the app carries on.
 */
export function cacheAppearance(appearance: ResolvedAppearance, cssVars: CssVars): void {
  try {
    window.localStorage.setItem(BOOT_APPEARANCE_KEY, appearance);
    window.localStorage.setItem(BOOT_THEME_VARS_KEY, JSON.stringify(cssVars));
  } catch {
    // See above.
  }
}

/**
 * Forget it again — used when the beta is switched off for this organization, so the next cold
 * start does not paint a dark first frame for an app that no longer has a dark appearance.
 */
export function clearCachedAppearance(): void {
  try {
    window.localStorage.removeItem(BOOT_APPEARANCE_KEY);
    window.localStorage.removeItem(BOOT_THEME_VARS_KEY);
  } catch {
    // See above.
  }
}
